import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  TableHead,
  Collapse,
  Checkbox,
  SortDirection,
  TableSortLabel,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  FirstPage as FirstPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage as LastPageIcon,
} from '@mui/icons-material';

interface DataGridCompProps {
  rows: any[];
  columns: any[];
  paginationModel: { page: number; pageSize: number };
  handleRowClick: (data: any) => void;
  handlePagination: (data: { offset: number; limit: number }) => void;
  totalRecords: number;
  handleStatus: (data: any, event?: any) => void;
  customClick?: (data: any) => void;
  isPagingHide?: boolean;
  isMore?: boolean;
  moreOpen?: number;
  orderBy?: string;
  order?: SortDirection;
  handleCheckBoxChange?: (data: any) => void;
  currentLocation?: any;
  handleOrderBy?: (columnName: any) => void;
}

// Custom Pagination Actions Component
const TablePaginationActions: React.ComponentType<any> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
}) => {
  const theme = useTheme();

  const handlePageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => {
    onPageChange(event, newPage);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={(event) => handlePageButtonClick(event, 0)}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={(event) => handlePageButtonClick(event, page - 1)}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={(event) => handlePageButtonClick(event, page + 1)}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={(event) =>
          handlePageButtonClick(
            event,
            Math.max(0, Math.ceil(count / rowsPerPage) - 1)
          )
        }
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

// Main Custom Table Component
const CustomPaginationActionsTable: React.FC<DataGridCompProps> = ({
  rows,
  columns,
  paginationModel,
  handleRowClick,
  handlePagination,
  totalRecords,
  handleStatus,
  customClick,
  isPagingHide,
  isMore,
  moreOpen,
  handleCheckBoxChange,
  currentLocation,
  orderBy,
  order,
  handleOrderBy,
}) => {
  const [page, setPage] = useState(paginationModel.page);
  const [rowsPerPage, setRowsPerPage] = useState(paginationModel.pageSize);
  const [checkedIds, setCheckedIds] = useState<any[]>([]);

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    let aVal: any = a[orderBy];
    let bVal: any = b[orderBy];

    // Handle numeric strings (e.g., price)
    if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
      aVal = Number(aVal);
      bVal = Number(bVal);
    }

    if (bVal < aVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }

  type Order = 'asc' | 'desc';

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const visibleRows = React.useMemo(
    () =>
      [...rows].sort(
        getComparator(order ? order : 'asc', orderBy ? orderBy : 'id')
      ),
    [order, orderBy, rows]
  );
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    const offsetData = newPage * rowsPerPage;
    setPage(newPage);
    handlePagination({ offset: offsetData, limit: rowsPerPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    handlePagination({ offset: 0, limit: newRowsPerPage });
    console.log(event.target.value);
  };
  const handleCheckBox = (checkId: any) => {
    setCheckedIds((prevItems: any) => {
      const newItems = [...prevItems];
      if (!newItems.includes(checkId)) {
        newItems.push(checkId);
      } else {
        newItems.splice(newItems.indexOf(checkId), 1);
      }
      if (handleCheckBoxChange) handleCheckBoxChange(newItems);
      return newItems;
    });
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
        <TableHead style={{ background: ' rgba(0, 244, 255, 0.06)' }}>
          <TableRow>
            {columns.map((column: any, indexc) => (
              <TableCell
                sortDirection={orderBy === column.field ? order : false}
                key={'tblcell_' + column.field + '_' + indexc}
                style={{}}
              >
                {column.disableSort ? (
                  <span>{column.headerName}</span>
                ) : (
                  <TableSortLabel
                    direction={
                      orderBy === column.field ? (order ? order : 'asc') : 'asc'
                    }
                    active={orderBy === column.field}
                    onClick={() => {
                      if (handleOrderBy) {
                        handleOrderBy(column.field);
                      }
                    }}
                  >
                    {column.headerName}
                    {orderBy === column.field ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {(orderBy ? visibleRows : rows).map((row, index: number) => (
          <TableBody>
            <TableRow
              sx={{ cursor: 'pointer' }}
              key={'tblrowbody_' + row.id || 'tblrowbody_' + row.name}
              onClick={(event: any) => {
                const currentClass: any = event.target.className;

                if (event.target.type === 'checkbox') {
                  event.preventDefault();
                  handleStatus(row, event);
                } else {
                  if (typeof currentClass === 'string') {
                    if (currentClass.indexOf('MuiTableCell-root') !== -1) {
                      handleRowClick(row);
                    } else {
                      if (customClick) {
                        customClick(row);
                      }
                    }
                  } else {
                    if (customClick) {
                      customClick(row);
                    }
                  }
                }
              }}
            >
              {columns.map((column, index: any) => (
                <TableCell
                  key={'tblbodycell_' + index + '_' + column.field}
                  style={{
                    width: column.width ? column.width : 'auto',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {column.renderCell
                    ? column.renderCell(row[column.field], row)
                    : column.valueGetter
                      ? column.valueGetter(row[column.field], row)
                      : (row[column.field] ?? '-')}
                </TableCell>
              ))}
            </TableRow>
            {isMore && (
              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={10}
                >
                  <Collapse
                    in={moreOpen === index}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        margin: 1,
                        border: 1,
                        borderColor: '#efeeed',
                        borderRadius: 1,
                      }}
                    >
                      <Table>
                        <TableHead
                          style={{ background: ' rgba(0, 244, 255, 0.06)' }}
                        >
                          <TableRow>
                            <TableCell colSpan={2}>Stock Level</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {row.stockLocations.map(
                            (detailItem: any, indexDetail: number) => {
                              return (
                                <TableRow
                                  key={`location_${indexDetail}_${index}`}
                                >
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{ width: '73%' }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'anchor-center',
                                      }}
                                    >
                                      {currentLocation !== 0 && (
                                        <Checkbox
                                          edge="start"
                                          checked={checkedIds.includes(
                                            row.resourceId
                                          )}
                                          tabIndex={-1}
                                          value={row.resourceId}
                                          onChange={() =>
                                            handleCheckBox(row.resourceId)
                                          }
                                        />
                                      )}

                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'anchor-center',
                                        }}
                                      >
                                        {detailItem.locationName}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                                    {detailItem.stockLevel}
                                  </TableCell>
                                </TableRow>
                              );
                            }
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        ))}

        {!isPagingHide && (
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[2, 5, 10, 25, { label: 'All', value: -1 }]}
                colSpan={columns.length}
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                sx={{
                  '& .MuiInputBase-root.MuiInputBase-colorPrimary': {
                    marginTop: '15px',
                  },
                }}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};

export default CustomPaginationActionsTable;
